import {
  AutoSelection,
  Combobox,
  Icon,
  InlineStack,
  Listbox,
  Tag,
} from "@shopify/polaris"
import { PlusCircleIcon, SelectIcon } from "@shopify/polaris-icons"
import classNames from "classnames"
import { customMatch, Option, useScrollRef } from "polaris-enhance"
import { useCallback, useMemo, useState } from "react"

import { useValidate } from "../global"
import { ProTextFieldProps } from "../ProTextFiled/ProTextField"
import Styles from "./index.module.scss"

export type ComboOption = Option & { node?: React.ReactNode }
export interface ProComboSelectMultiWithAddProps
  extends Omit<ProTextFieldProps, "value" | "onChange"> {
  value: string[]
  onChange(value: string[]): void
  required?: boolean
  maxNum?: number // 最大添加数量 默认5000
  cutByComma?: boolean // 是否按逗号分割
}
export function ProComboSelectMultiWithAdd(
  props: ProComboSelectMultiWithAddProps,
) {
  const {
    label,
    value = [],
    required,
    onChange,
    suffix = <Icon source={SelectIcon}></Icon>,
    maxNum = 5000,
    cutByComma = true,
    ...textFieldProps
  } = props
  const { scrollToRef, scrooledRef } = useScrollRef()

  const [inputValue, setInputValue] = useState("")
  const [isClickingSuffix, setIsClickingSuffix] = useState(false)

  const options = useMemo(() => {
    return value.filter((item) => customMatch(item, inputValue))
  }, [inputValue, value])

  const updateSelection = useCallback(
    (val: string) => {
      if (value.includes(val)) {
        onChange(value.filter((item) => item !== val))
      } else {
        onChange([...value, ...(cutByComma ? val.split(",") : [val])])
        if (val === inputValue) {
          setInputValue("")
        }
      }
      scrollToRef()
    },
    [cutByComma, inputValue, onChange, scrollToRef, value],
  )

  const contentMarkup =
    value.length > 0 ? (
      <InlineStack gap={"100"}>
        {value.map((v) => {
          return (
            <Tag
              key={v}
              onRemove={() => {
                const newValue = value.filter((item) => item !== v)
                onChange(newValue)
              }}
            >
              {v}
            </Tag>
          )
        })}
      </InlineStack>
    ) : null

  const { id, error, handleFocus, handleBlur } = useValidate({
    value,
    onlyRequired: required,
    id: textFieldProps.id,
  })

  // 处理点击 suffix 触发下拉框
  const handleSuffixMouseDown = useCallback(
    (event: React.MouseEvent) => {
      // 阻止事件冒泡，防止触发失焦事件
      event.preventDefault()
      event.stopPropagation()
      setIsClickingSuffix(true)

      // 通过 DOM 查询找到输入框并触发点击
      const inputElement = document.getElementById(id)
      if (inputElement) {
        inputElement.focus()
        inputElement.click()
      }
    },
    [id],
  )

  const handleSuffixMouseUp = useCallback(() => {
    setIsClickingSuffix(false)
  }, [])

  return (
    <div
      className={classNames("space-y-2", Styles.TextFieldSuffix)}
      ref={scrooledRef}
    >
      <Combobox
        allowMultiple
        activator={
          <Combobox.TextField
            {...textFieldProps}
            id={id}
            error={error}
            onChange={setInputValue}
            label={label}
            value={inputValue}
            autoComplete="off"
            onBlur={() => {
              // 如果正在点击 suffix，不触发失焦事件
              if (!isClickingSuffix) {
                handleBlur()
              }
            }}
            onFocus={handleFocus}
            suffix={
              <div
                onMouseDown={handleSuffixMouseDown}
                onMouseUp={handleSuffixMouseUp}
                className="cursor-pointer"
                style={{ "--p-space-300": "0" }}
              >
                {suffix}
              </div>
            }
          />
        }
        onClose={() => {
          setInputValue("")
          handleBlur()
        }}
      >
        {inputValue || value.length > 0 ? (
          <Listbox
            onSelect={updateSelection}
            autoSelection={AutoSelection.None}
          >
            {!!inputValue &&
              !value.includes(inputValue) &&
              value.length < maxNum && (
                <Listbox.Action value={inputValue}>
                  <div className="flex items-center gap-1 py-1">
                    <div>
                      <Icon source={PlusCircleIcon} />
                    </div>
                    {t("AddInputValue", {
                      replace: {
                        x: inputValue,
                      },
                    })}
                  </div>
                </Listbox.Action>
              )}
            {options.map((v) => {
              return (
                <Listbox.Option
                  key={`${v}`}
                  value={v}
                  selected={value.includes(v)}
                  accessibilityLabel={label}
                >
                  <Listbox.TextOption selected={value.includes(v)}>
                    {v}
                  </Listbox.TextOption>
                </Listbox.Option>
              )
            })}
          </Listbox>
        ) : (
          <div className="px-4 py-2">{t("NoResultsFound")}</div>
        )}
      </Combobox>
      {contentMarkup}
    </div>
  )
}
